// ✅ Additional imports and code unchanged
import React, { useState, useEffect } from 'react';
import { Button, Form, Container, Spinner, Modal } from 'react-bootstrap';
import { firestore } from '../../utils/firebase';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, getDocs, orderBy, limit, setDoc } from 'firebase/firestore';
import useRole from '../../hooks/useRole';
import logoImage from '../../../Nucleus Logo PPTX.png';

const CreateJobCard = () => {
    const [equipmentNumber, setEquipmentNumber] = useState('');
    const [description, setDescription] = useState('');
    const [assignedEmployeeId, setAssignedEmployeeId] = useState('');
    const [plannedDate, setPlannedDate] = useState('');
    const [priority, setPriority] = useState('Medium');
    const [scheduled, setScheduled] = useState(false);                      // ✅ NEW
    const [employees, setEmployees] = useState([]);
    const [equipmentList, setEquipmentList] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [nextJobCardNumber, setNextJobCardNumber] = useState('');
    const navigate = useNavigate();
    const { role, loading } = useRole();

    useEffect(() => {
        const fetchData = async () => {
            const auth = getAuth();
            const currentUser = auth.currentUser;
            if (!currentUser) return;

            const userDoc = await getDoc(doc(firestore, 'users', currentUser.uid));
            const company = userDoc.data().company;

            const usersSnapshot = await getDocs(query(collection(firestore, 'users')));
            const users = usersSnapshot.docs
                .filter(doc => doc.data().company === company)
                .map(doc => ({
                    id: doc.id,
                    name: `${doc.data().name} ${doc.data().surname}`
                }));
            setEmployees(users);

            const equipmentSnapshot = await getDocs(collection(firestore, 'companies', company, 'equipment'));
            const equipment = equipmentSnapshot.docs.map(doc => doc.data().equipmentNumber);
            setEquipmentList(equipment);

            const jobCardsRef = collection(firestore, 'companies', company, 'JobCards');
            const latestQuery = query(jobCardsRef, orderBy('createdAt', 'desc'), limit(1));
            const latestSnapshot = await getDocs(latestQuery);
            let nextNumber = 1;
            if (!latestSnapshot.empty) {
                const lastNumber = latestSnapshot.docs[0].data().jobCardNumber?.replace('MB-JC-', '') ?? '0';
                nextNumber = parseInt(lastNumber, 10) + 1;
            }
            setNextJobCardNumber(`MB-JC-${nextNumber.toString().padStart(4, '0')}`);
        };
        fetchData();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!equipmentNumber || !description || !assignedEmployeeId || !plannedDate) return;
        setIsSubmitting(true);

        try {
            const auth = getAuth();
            const currentUser = auth.currentUser;
            const userDoc = await getDoc(doc(firestore, 'users', currentUser.uid));
            const company = userDoc.data().company;

            const plannedDateObj = new Date(plannedDate);
            const formattedDate = plannedDateObj.toISOString().split('T')[0];  // yyyy-mm-dd
            const recordId = `${equipmentNumber}-${description}-${formattedDate}`;

            // ✅ Always create Job Card
            await setDoc(doc(firestore, 'companies', company, 'JobCards', nextJobCardNumber), {
                jobCardNumber: nextJobCardNumber,
                equipmentNumber,
                description,
                assignedEmployeeId,
                assignedEmployeeName: employees.find(emp => emp.id === assignedEmployeeId)?.name || '',
                priority,
                scheduled,
                status: 'Open',
                createdBy: currentUser.uid,
                createdAt: new Date(),
                plannedDate: plannedDateObj
            });

            // ✅ Always create maintenanceLogs
            await setDoc(doc(firestore, 'companies', company, 'maintenanceLogs', recordId), {
                equipmentNumber,
                activity: description,
                employee: employees.find(emp => emp.id === assignedEmployeeId)?.name || '',
                date: plannedDateObj,
                scheduled,                             // ✅ scheduled: true or false
                ScheduledApproved: false,
                JobcardOpen: true,
                fullyApproved: false,
                approved: false,
                rejected: false,
                logmaintenance: false,
                Confirmed: true,
                linkedJobCardNumber: nextJobCardNumber,
                vendor: '',
                maintenanceType: '',
                maintenanceDuration: { quantity: '', unit: '' },
                downtime: { quantity: '', unit: '' },
                comment: '',
                proofUrl: '',
            });

            // ✅ Reset form + show success
            setEquipmentNumber('');
            setDescription('');
            setAssignedEmployeeId('');
            setPlannedDate('');
            setPriority('Medium');
            setScheduled(false);
            setShowModal(true);

        } catch (error) {
            console.error('Error creating job card & maintenance log:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleModalClose = () => {
        setShowModal(false);
        navigate('/equipment-maintenance');
    };

    if (loading) {
        return (
            <div className="loading-spinner-container">
                <Spinner animation="border" role="status" variant="success" className="loading-spinner">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        );
    }

    if (role !== 'Admin') {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <h3>You do not have permission to access this page.</h3>
            </Container>
        );
    }

    return (
        <Container className="d-flex justify-content-center align-items-center mb-5" style={{ minHeight: '60vh' }}>
            <img src={logoImage} alt="Company Logo" style={{ maxWidth: '100px', margin: '10px' }} />
            <h1 className="mb-5">Create Job Card</h1>
            <Form onSubmit={handleSubmit}>
                <Form.Group className="my-3">
                    <Form.Label>Auto Job Card Number</Form.Label>
                    <Form.Control type="text" value={nextJobCardNumber} readOnly />
                </Form.Group>

                <Form.Group className="my-3">
                    <Form.Label>Equipment Number</Form.Label>
                    <Form.Select value={equipmentNumber} onChange={e => setEquipmentNumber(e.target.value)} required>
                        <option value="">Select Equipment</option>
                        {equipmentList.map((equip, index) => (
                            <option key={index} value={equip}>{equip}</option>
                        ))}
                    </Form.Select>
                </Form.Group>

                <Form.Group className="my-3">
                    <Form.Label>Assign To Employee</Form.Label>
                    <Form.Select value={assignedEmployeeId} onChange={e => setAssignedEmployeeId(e.target.value)} required>
                        <option value="">Select Employee</option>
                        {employees.map(emp => (
                            <option key={emp.id} value={emp.id}>{emp.name}</option>
                        ))}
                    </Form.Select>
                </Form.Group>

                <Form.Group className="my-3">
                    <Form.Label>Planned Date</Form.Label>
                    <Form.Control type="date" value={plannedDate} onChange={e => setPlannedDate(e.target.value)} required />
                </Form.Group>

                <Form.Group className="my-3">
                    <Form.Label>Priority</Form.Label>
                    <Form.Select value={priority} onChange={e => setPriority(e.target.value)}>
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                    </Form.Select>
                </Form.Group>

                {/* ✅ NEW Checkbox */}
                <Form.Group className="my-3">
                    <Form.Check
                        type="checkbox"
                        label="Scheduled Maintenance"
                        checked={scheduled}
                        onChange={e => setScheduled(e.target.checked)}
                    />
                </Form.Group>

                <Form.Group className="my-3">
                    <Form.Label>Description of Work</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        required
                    />
                </Form.Group>

                <Button variant="success" type="submit" style={{ width: '175px' }} disabled={isSubmitting}>
                    {isSubmitting ? (
                        <Spinner as="span" animation="border" size="sm" role="status" />
                    ) : (
                        'Create Job Card'
                    )}
                </Button>

                <Button variant="outline-success" onClick={() => navigate('/equipment-maintenance')} style={{ marginLeft: '10px', width: '175px' }}>
                    Back
                </Button>
            </Form>

            <Modal show={showModal} onHide={handleModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Success</Modal.Title>
                </Modal.Header>
                <Modal.Body>Job Card {nextJobCardNumber} created successfully!</Modal.Body>
                <Modal.Footer>
                    <Button variant="success" onClick={() => setShowModal(false)}>Add Another</Button>
                    <Button variant="success" onClick={handleModalClose}>Back to Maintenance</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default CreateJobCard;
